import { useState } from 'react';
import { Spinner } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { downloadIncidentPDF, downloadIncidentPDFWithToken } from '../Generate/Components/FormikHelpers/Api';

/**
 * IncidentPDFDownloadButton
 *
 * Renders a "Download PDF" button for an incident report.
 *
 * Props:
 *  - reportId   (string)  — MongoDB ObjectId of the report
 *  - token      (string|null) — preview JWT; if present, token-based download is used
 *                               (PDF content is scoped by token permissions)
 *  - disabled   (bool)    — externally controlled disabled state
 *  - label      (string)  — optional custom label
 *  - variant    ('button'|'icon') — rendering variant
 */
const IncidentPDFDownloadButton = ({
  reportId,
  token = null,
  disabled = false,
  label = 'Download PDF',
  variant = 'button',
}) => {
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    if (!reportId || loading || disabled) return;
    setLoading(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      if (token) {
        await downloadIncidentPDFWithToken(token, tz);
      } else {
        await downloadIncidentPDF(reportId, tz);
      }
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'icon') {
    return (
      <button
        type="button"
        title={label}
        onClick={handleDownload}
        disabled={disabled || loading}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 36, height: 36, borderRadius: 9,
          background: disabled ? '#f1f5f9' : '#f0fdf4',
          border: `1px solid ${disabled ? '#e2e8f0' : '#86efac'}`,
          color: disabled ? '#94a3b8' : '#16a34a',
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          transition: 'all 0.15s',
        }}
      >
        {loading ? <Spinner size="sm" /> : <FeatherIcon icon="download" size={15} />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={disabled || loading}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '9px 18px',
        background: disabled || loading
          ? '#f1f5f9'
          : 'linear-gradient(135deg, #059669, #047857)',
        color: disabled || loading ? '#94a3b8' : '#fff',
        border: 'none', borderRadius: 10,
        fontSize: 13, fontWeight: 600,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        boxShadow: disabled || loading ? 'none' : '0 2px 8px rgba(5,150,105,0.25)',
        transition: 'all 0.2s',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      {loading ? (
        <><Spinner size="sm" color={disabled ? 'secondary' : 'light'} /> Generating…</>
      ) : (
        <><FeatherIcon icon="download" size={14} /> {label}</>
      )}
    </button>
  );
};

export { IncidentPDFDownloadButton };